import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import {
  Box, Typography, Paper, Button, Slider, TextField, IconButton, CircularProgress,
  List, ListItem, ListItemText, Divider, Alert, Fab, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

// Hook per Speech Recognition
const useSpeechRecognition = () => {
  const { t } = useTranslation('driver');
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window)) {
      console.warn(t('speech_not_supported'));
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'it-IT'; // Default italiano

    recognitionRef.current.onresult = (event) => {
      let currentTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          currentTranscript += event.results[i][0].transcript;
        }
      }
      if (currentTranscript) {
         setTranscript(prev => prev + ' ' + currentTranscript);
      }
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };
  }, []);

  const startListening = () => {
    setTranscript('');
    setIsListening(true);
    recognitionRef.current?.start();
  };

  const stopListening = () => {
    setIsListening(false);
    recognitionRef.current?.stop();
  };

  return { isListening, transcript, startListening, stopListening, setTranscript };
};

const CornerFeedback = ({ corner, index, updateCorner, removeCorner }) => {
  const { t } = useTranslation('driver');
  const { isListening, transcript, startListening, stopListening, setTranscript } = useSpeechRecognition();

  // Quando il transcript cambia (finalizzato), aggiorniamo il testo delle note
  useEffect(() => {
    if (transcript) {
        updateCorner(corner.id, 'notes', (corner.notes ? corner.notes + ' ' : '') + transcript.trim());
        setTranscript(''); // Consuma il transcript
    }
  }, [transcript]);

  return (
    <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'primary.light', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField 
            variant="standard" 
            value={corner.name} 
            onChange={(e) => updateCorner(corner.id, 'name', e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: '1.2rem', fontWeight: 'bold' } }}
        />
        <IconButton color="error" onClick={() => removeCorner(corner.id)} size="small">
          <DeleteIcon />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary">{t('corner_entry')}</Typography>
      <Slider 
        value={corner.entry} 
        onChange={(e, val) => updateCorner(corner.id, 'entry', val)} 
        step={1} marks min={1} max={10} valueLabelDisplay="auto" color="primary" 
      />

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>{t('corner_mid')}</Typography>
      <Slider 
        value={corner.mid} 
        onChange={(e, val) => updateCorner(corner.id, 'mid', val)} 
        step={1} marks min={1} max={10} valueLabelDisplay="auto" color="secondary" 
      />

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>{t('corner_exit')}</Typography>
      <Slider 
        value={corner.exit} 
        onChange={(e, val) => updateCorner(corner.id, 'exit', val)} 
        step={1} marks min={1} max={10} valueLabelDisplay="auto" color="success" 
      />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2 }}>
        <TextField
          label={t('notes_label')}
          multiline rows={2} fullWidth
          value={corner.notes}
          onChange={(e) => updateCorner(corner.id, 'notes', e.target.value)}
        />
        <IconButton 
            color={isListening ? "error" : "primary"} 
            onClick={isListening ? stopListening : startListening}
            sx={{ bgcolor: isListening ? 'error.light' : 'action.hover', width: 56, height: 56 }}
        >
          {isListening ? <MicOffIcon /> : <MicIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

const DriverIssueReport = ({ teamId, vehicleId }) => {
  const { t } = useTranslation('driver');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);
  const { isListening, transcript, startListening, stopListening, setTranscript } = useSpeechRecognition();

  useEffect(() => {
    if (transcript) {
        setDescription(prev => (prev ? prev + ' ' : '') + transcript.trim());
        setTranscript('');
    }
  }, [transcript]);

  const handleSendIssue = async () => {
    if (!description.trim()) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "vehicleIssues"), {
        teamId,
        vehicleId,
        description: description.trim(),
        status: 'Aperto',
        source: 'Driver',
        createdAt: serverTimestamp()
      });
      setSuccessMsg(true);
      setDescription('');
      setTimeout(() => setSuccessMsg(false), 3000);
    } catch (e) {
      console.error(e);
      alert(t('issue_report_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 4, border: '2px dashed', borderColor: 'error.main', position: 'relative', bgcolor: 'error.50' }}>
      <Typography variant="h6" color="error.main" gutterBottom>
        {t('issue_report_title')}
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <TextField 
          multiline 
          rows={3} 
          fullWidth 
          variant="outlined" 
          placeholder={t('issue_report_placeholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2, bgcolor: 'background.paper' }}
        />
        <IconButton 
          color={isListening ? "error" : "primary"} 
          onClick={isListening ? stopListening : startListening}
          sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'background.paper', boxShadow: 1 }}
        >
          {isListening ? <MicOffIcon /> : <MicIcon />}
        </IconButton>
      </Box>
      <Button 
        variant="contained" 
        color="error" 
        onClick={handleSendIssue} 
        disabled={isSubmitting || !description.trim()}
        fullWidth
      >
        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('issue_report_button')}
      </Button>
      {successMsg && <Alert severity="success" sx={{ mt: 2 }}>{t('issue_report_success')}</Alert>}
    </Paper>
  );
};

function DriverDashboard({ userId, teamId, mode, teamLogos }) {
  const { t } = useTranslation('driver');
  const [vehicle, setVehicle] = useState(null);
  const [allSetups, setAllSetups] = useState([]);
  const [selectedSetupId, setSelectedSetupId] = useState('');
  const [driverName, setDriverName] = useState('Pilota');
  const [isLoading, setIsLoading] = useState(true);
  const [corners, setCorners] = useState([{ id: Date.now(), name: "Curva 1", entry: 5, mid: 5, exit: 5, notes: "" }]);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchVehicle = async () => {
      if (!teamId || !userId) return;
      try {
        // Recupera nome utente
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const ud = userSnap.data();
          setDriverName(ud.nomeCompleto || ud.email || 'Pilota');
        }

        // Cerca l'assegnazione per questo utente
        const assignQ = query(collection(db, "vehicleAssignments"), where("teamId", "==", teamId), where("userId", "==", userId));
        const assignSnap = await getDocs(assignQ);
        
        if (assignSnap.empty) {
            setIsLoading(false);
            return; // Nessun veicolo assegnato
        }

        const assignedVehicleId = assignSnap.docs[0].data().vehicleId;
        
        // Ora recupera il veicolo
        const q = query(collection(db, "vehicles"), where("teamId", "==", teamId));
        const snap = await getDocs(q);
        
        const vehicleDoc = snap.docs.find(d => d.id === assignedVehicleId);
        if (vehicleDoc) {
          const veh = { id: vehicleDoc.id, ...vehicleDoc.data() };
          setVehicle(veh);
          
          // Cerca l'ultimo setup creato per quest'auto
          const setupQ = query(
             collection(db, "setupSheets"), 
             where("vehicleId", "==", veh.id), 
             where("teamId", "==", teamId)
          );
          const setupSnap = await getDocs(setupQ);
          if (!setupSnap.empty) {
             const setups = setupSnap.docs.map(d => ({ id: d.id, ...d.data() }));
             setups.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
             setAllSetups(setups);
             setSelectedSetupId(setups[0].id);
          }
        }
      } catch (err) {
        console.error("Errore recupero auto:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchVehicle();
  }, [teamId, userId]);

  // Fetch delle curve preimpostate del tracciato per la sessione selezionata
  useEffect(() => {
    const fetchTrackCorners = async () => {
      const selectedSetup = allSetups.find(s => s.id === selectedSetupId);
      if (!selectedSetup || !selectedSetup.eventId) {
        setCorners([{ id: Date.now(), name: "Curva 1", entry: 5, mid: 5, exit: 5, notes: "" }]);
        return;
      }
      
      try {
        const eventDoc = await getDoc(doc(db, "events", selectedSetup.eventId));
        if (eventDoc.exists()) {
          const trackId = eventDoc.data().trackId;
          if (trackId) {
            const trackDoc = await getDoc(doc(db, "tracks", trackId));
            if (trackDoc.exists() && trackDoc.data().corners && trackDoc.data().corners.length > 0) {
              const trackCorners = trackDoc.data().corners;
              setCorners(trackCorners.map((name, index) => ({
                id: Date.now() + index,
                name: name,
                entry: 5, mid: 5, exit: 5, notes: ""
              })));
              return;
            }
          }
        }
      } catch (e) {
        console.error("Errore fetch track corners:", e);
      }
      
      // Fallback
      setCorners([{ id: Date.now(), name: "Curva 1", entry: 5, mid: 5, exit: 5, notes: "" }]);
    };
    
    if (selectedSetupId && allSetups.length > 0) {
      fetchTrackCorners();
    }
  }, [selectedSetupId, allSetups]);

  const handleAddCorner = () => {
    setCorners([...corners, { id: Date.now(), name: `Curva ${corners.length + 1}`, entry: 5, mid: 5, exit: 5, notes: "" }]);
  };

  const updateCorner = (id, field, value) => {
    setCorners(corners.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const removeCorner = (id) => {
    setCorners(corners.filter(c => c.id !== id));
  };

  const handleSaveFeedback = async () => {
    const selectedSetup = allSetups.find(s => s.id === selectedSetupId);
    if (!vehicle || !selectedSetup) return;
    setIsSaving(true);
    try {
      await addDoc(collection(db, "driverFeedback"), {
        teamId,
        vehicleId: vehicle.id,
        setupId: selectedSetup.id,
        sessionName: selectedSetup.sessionName || t('unknown_session'),
        driverId: userId,
        driverName: driverName,
        createdAt: serverTimestamp(),
        isUnread: true, // Flag per le notifiche all'ingegnere
        corners: corners
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // Triggera un reset forzato rifacendo il check delle curve
      setSelectedSetupId(prev => {
        const current = prev;
        return current; // react ricaricherà se serve o possiamo resettare
      });
      // Facciamo un reset fallback rapido, per pulire i valori ma mantenere i nomi
      setCorners(prev => prev.map(c => ({...c, entry: 5, mid: 5, exit: 5, notes: ""})));
    } catch (err) {
      console.error(err);
      alert("Errore salvataggio feedback");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;

  // Determina quale logo usare
  const logoSrc = mode === 'dark' 
    ? (teamLogos?.dark || teamLogos?.light) 
    : (teamLogos?.light || teamLogos?.dark);

  if (!vehicle) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        {logoSrc && (
          <Box sx={{ mb: 3 }}>
            <img src={logoSrc} alt="Team Logo" style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }} />
          </Box>
        )}
        <Typography variant="h5" color="text.secondary">{t('no_vehicle_assigned_title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('no_vehicle_assigned_subtitle')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', pb: 10 }}>
      {logoSrc && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 2 }}>
          <img src={logoSrc} alt="Team Logo" style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }} />
        </Box>
      )}
      <Paper sx={{ p: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <DirectionsCarIcon fontSize="large" />
        <Box>
            <Typography variant="h5" fontWeight="bold">{vehicle.marca} {vehicle.modello} #{vehicle.numeroGara}</Typography>
            {allSetups.length > 0 ? (
                <FormControl variant="standard" sx={{ mt: 1, minWidth: 200 }}>
                  <InputLabel sx={{ color: 'primary.contrastText', '&.Mui-focused': { color: 'primary.contrastText' } }}>{t('session_setup_label')}</InputLabel>
                  <Select
                    value={selectedSetupId}
                    onChange={(e) => setSelectedSetupId(e.target.value)}
                    sx={{ color: 'primary.contrastText', '& .MuiSvgIcon-root': { color: 'primary.contrastText' }, '&:before': { borderColor: 'primary.contrastText' }, '&:hover:not(.Mui-disabled):before': { borderColor: 'primary.contrastText' } }}
                  >
                    {allSetups.map(setup => (
                      <MenuItem key={setup.id} value={setup.id}>
                        {setup.sessionName || t('unnamed_setup')} ({setup.createdAt?.toDate().toLocaleDateString()})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            ) : (
                <Typography variant="body2" sx={{ mt: 0.5, color: 'error.main', bgcolor: 'white', px: 1, borderRadius: 1 }}>
                  {t('no_setup_found')}
                </Typography>
            )}
        </Box>
      </Paper>

      {success && <Alert severity="success" sx={{ mb: 3 }}>{t('feedback_success')}</Alert>}

      {allSetups.length === 0 ? null : (
        <>
          {corners.map((corner, index) => (
            <CornerFeedback 
                key={corner.id} 
                corner={corner} 
                index={index} 
                updateCorner={updateCorner} 
                removeCorner={removeCorner} 
            />
          ))}

          <Button 
            variant="outlined" 
            startIcon={<AddIcon />} 
            onClick={handleAddCorner} 
            fullWidth 
            sx={{ borderStyle: 'dashed', py: 2, mb: 4 }}
          >
            {t('add_corner')}
          </Button>

          <Divider sx={{ my: 4 }} />
          <DriverIssueReport teamId={teamId} vehicleId={vehicle?.id} />

          <Box sx={{ position: 'fixed', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 1000, px: 2 }}>
            <Fab 
              color="primary" 
              variant="extended" 
              onClick={handleSaveFeedback} 
              disabled={isSaving || corners.length === 0}
              sx={{ width: '100%', maxWidth: 400, fontWeight: 'bold' }}
            >
              {isSaving ? <CircularProgress size={24} color="inherit" /> : <><SaveIcon sx={{ mr: 1 }} /> {t('save_send_feedback')}</>}
            </Fab>
          </Box>
        </>
      )}
    </Box>
  );
}

export default DriverDashboard;
